import { useEffect, useState } from "react";
import { ArrowRightCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  type DossierStatut,
  getApiErrorDetail,
  getLegalTransitions,
  postValidateStep,
} from "@/lib/api";
import { classifyApiError } from "@/hooks/useApiErrorState";

const MARINE_DEEP = "oklch(0.115 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";
const TEXT_DIM = "oklch(0.85 0.01 243)";

const LABELS: Record<DossierStatut, string> = {
  OUVERT: "OUVERT (recevabilite a valider)",
  INSTRUCTION: "INSTRUCTION (delai 90j enclenche)",
  CLOTURE: "CLOTURE (debats clos, en delibere)",
  TERMINE: "TERMINE (sentence rendue, archivage)",
};

interface ValidateStepDialogProps {
  caseId: number;
  currentState: string;
  disabled?: boolean;
  onValidated: () => void;
}

// Wizard de transition workflow OHADA. Charge les transitions legales
// depuis l'endpoint /legal-transitions (P1.1 backend) -- ne propose
// QUE des etats reellement acceptes par WORKFLOW_TRANSITIONS, evite
// les 400 "Transition illegale" et donne a l'arbitre une vision
// claire du graphe d'etat OHADA.
export function ValidateStepDialog({
  caseId,
  currentState,
  disabled,
  onValidated,
}: ValidateStepDialogProps) {
  const [open, setOpen] = useState(false);
  const [transitions, setTransitions] = useState<DossierStatut[] | null>(null);
  const [selected, setSelected] = useState<DossierStatut | "">("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charge a l'ouverture pour afficher l'etat le plus frais (le
  // dossier a pu changer depuis le rendu parent, ex. autre arbitre).
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setSelected("");
    getLegalTransitions(caseId)
      .then((res) => setTransitions(res.legal_transitions))
      .catch((err) => {
        const classified = classifyApiError(err);
        if (classified.kind !== "none") {
          setError(classified.detail);
        }
      })
      .finally(() => setLoading(false));
  }, [open, caseId]);

  const onConfirm = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      await postValidateStep(caseId, selected as DossierStatut);
      toast.success(`Dossier passe en ${selected}.`);
      onValidated();
      setOpen(false);
    } catch (err) {
      const classified = classifyApiError(err);
      const errDetail =
        classified.kind !== "none"
          ? classified.detail
          : getApiErrorDetail(err) ?? "Echec transition.";
      toast.error(errDetail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabled} style={{ color: GOLD }}>
          <ArrowRightCircle size={14} className="mr-2" />
          Faire avancer la procedure
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Transition workflow OHADA</DialogTitle>
          <DialogDescription>
            Etat courant :{" "}
            <code style={{ color: GOLD }}>{currentState}</code>. Choisissez
            l'etat suivant parmi les transitions legales (source :
            WORKFLOW_TRANSITIONS).
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-6 flex items-center justify-center">
            <Loader2 className="animate-spin" size={20} />
          </div>
        )}

        {error && (
          <p className="text-sm" style={{ color: "rgb(254 202 202)" }}>
            {error}
          </p>
        )}

        {!loading && !error && transitions && (
          <>
            {transitions.length === 0 ? (
              <p className="text-sm" style={{ color: TEXT_DIM }}>
                Aucune transition legale depuis <strong>{currentState}</strong>.
                {currentState === "TERMINE"
                  ? " Le dossier est dans son etat terminal."
                  : ""}
              </p>
            ) : (
              <div className="space-y-3">
                <Select
                  value={selected}
                  onValueChange={(v) => setSelected(v as DossierStatut)}
                  disabled={submitting}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectionner le nouvel etat" />
                  </SelectTrigger>
                  <SelectContent>
                    {transitions.map((t) => (
                      <SelectItem key={t} value={t}>
                        {LABELS[t] ?? t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selected === "INSTRUCTION" && (
                  <p
                    className="text-xs"
                    style={{ color: GOLD }}
                  >
                    ⚠ Le passage en INSTRUCTION declenche un timer de 90
                    jours (delai Fast-Track OHADA). Une notification vous
                    sera envoyee a J-7.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={submitting}
          >
            Annuler
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!selected || submitting || transitions?.length === 0}
            style={{ background: GOLD, color: MARINE_DEEP }}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Validation...
              </>
            ) : (
              "Confirmer"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
